import React, {createContext, useContext, useEffect, useState} from 'react';
import {Form, FormikProvider, useFormik} from 'formik';
import {useIntl} from 'react-intl';
import {useParams} from 'react-router-dom';
import {useSelector} from 'react-redux';
import {useGlobalContext} from '../../../../common/components/core/GlobalContext';
import {useGlobalMessageContext} from '../../../../common/components/core/GlobalMessageContext';
import {useMessage} from '../../../../common/components/core/useGlobalMessage';
import {invoiceOrderTotalWithVat, isEmail} from '../../../../common/utils';
import {Currency, InvoiceOrderDocument, Option} from '../../../../common/types';
import {RootState} from '../../../../store/configureStore';
import {currencies} from '../../../../common/enums';
import {usePaymentContext} from '../PaymentContext';

export type PaymentPaypalContext = {
  currencyOptions: {label: string, value: Currency}[],
  loadInvoiceOptions: (input: string) => Promise<Option[]>
  onInvoiceChanged: (input: string) => Promise<void>
  loadCustomerOptions: (input: string) => Promise<Option[]>
  onCustomerChanged: (input: string) => Promise<void>
  updateForm: () => void
  loading: boolean
  paypalLink: string
  goToPaypal: () => void
}

export type PaymentPaypalContextProviderProps = {children: React.ReactNode}

export const defaultContext: PaymentPaypalContext = {
  currencyOptions: [],
  loadInvoiceOptions: async () => {
    return [];
  },
  onInvoiceChanged: async () => {
  },
  loadCustomerOptions: async () => {
    return [];
  },
  onCustomerChanged: async () => {
  },
  updateForm: async () => {
  },
  loading: false,
  paypalLink: '',
  goToPaypal: async () => {
  },
};

export type PaymentFormValues = {
  customerLink: string,
  currency: Currency,
  description: string,
  amount: string,
  invoiceID: string,
  phone: string,
  email: string,
  customerID: string,
  customerName: string,
}

export const defaultFormValues: PaymentFormValues = {
  customerLink: '',
  currency: 'ils',
  description: '',
  amount: '',
  invoiceID: '',
  phone: '',
  email: '',
  customerID: '',
  customerName: '',
};

export const PaymentPaypalContext = createContext<PaymentPaypalContext>(defaultContext);
PaymentPaypalContext.displayName = 'PaymentPaypalContext';

export const usePaymentPaypalContext = () => useContext(PaymentPaypalContext);

export const PaymentPaypalContextProvider = ({children}: PaymentPaypalContextProviderProps) => {
  const params = useParams();
  const M = useMessage();
  const intl = useIntl();
  const {apiService} = useGlobalContext();
  const globalMessage = useGlobalMessageContext();
  const E = useMessage();
  const currentLocale = useSelector((state: RootState) => state.i18n.locale);
  const [loading, setLoading] = useState<boolean>(false);
  const [paypalLink, setPaypalLink] = useState<string>('');

  const {invoiceOrder, updateLink} = usePaymentContext();

  const formik = useFormik<PaymentFormValues>({
    initialValues: {...defaultFormValues},
    validate: (values) => {
      const errors: Partial<{ [key in keyof PaymentFormValues]: string }> = {};
      if (!values.currency) errors.currency = M.requiredField;
      if (!values.description) errors.description = M.requiredField;
      if (!values.amount) errors.amount = M.requiredField;
      if (!values.customerName) errors.customerName = M.requiredField;
      if (!values.phone) errors.phone = M.requiredField;
      if (!values.email) errors.email = M.requiredField;
      if (values.email && !isEmail(values.email)) errors.email = E.notValidEmail;

      return errors;
    },
    onSubmit: async (values, {resetForm}) => {
      setLoading(true);
      try {
        const result = await apiService.getPaymentPaypalPage(
          currentLocale,
          values.currency,
          values.description,
          parseFloat(values.amount),
          params?.invoiceOrderID ? params.invoiceOrderID : '',
          values.invoiceID || '',
          values.phone || '',
          values.email || '',
          values.customerID || '',
          values.customerName || '',
        );
        if (result.errorCode < 0) {
          globalMessage.open(E.serverError);
        } else {
          updateLink('');
          setPaypalLink(result.body.url);
          resetForm();
        }
      } catch (e) {
        globalMessage.open(E.serverError);
      } finally {
        setLoading(false);
      }
    },
    enableReinitialize: true,
  });

  const update = async (invoiceOrderDTO: InvoiceOrderDocument) => {
    const invoice = {
      ...invoiceOrderDTO,
      description: invoiceOrderDTO.lines.map(line => line.description).join(', '),
      lines: invoiceOrderDTO.lines.map(line => ({
        ...line,
        amount: line.amount.toString(),
      }))
    };

    await formik.setValues({
      ...defaultFormValues,
      customerID: invoice.customerID,
      customerName: invoice.customerName,
      email: invoice.emailRecipients,
      phone: invoice.phone,
      description: invoice.description,
      amount: invoiceOrderTotalWithVat(invoice, new Date(invoice.createdAt)).toFixed(2)
    });
  };

  const updateForm = () => {
    if (invoiceOrder) {
      update(invoiceOrder).catch(console.error);
    }
  };

  useEffect(() => {
    updateForm();
  }, [invoiceOrder]);

  const currencyOptions = [
    {
      label: intl.formatMessage({
        id: 'payment-currency-NIS',
        defaultMessage: 'NIS'
      }),
      value: currencies.NIS
    },
    {
      label: intl.formatMessage({
        id: 'payment-currency-USD',
        defaultMessage: 'USD'
      }),
      value: currencies.USD
    },
    {
      label: intl.formatMessage({
        id: 'payment-currency-EUR',
        defaultMessage: 'EUR'
      }),
      value: currencies.EUR
    },
  ];

  const loadInvoiceOptions = async (input: string) => {
    try {
      const result = await apiService.getInvoiceOptions(input);
      if (result.errorCode < 0) {
        globalMessage.open(E.serverError);
        formik.setFieldValue('invoiceID', '');

        return [];
      } else {
        if (result.body.list.length === 0) {
          formik.setFieldValue('invoiceID', '');
        }

        return result.body.list;
      }
    } catch (e) {
      globalMessage.open(E.serverError);
      formik.setFieldValue('invoiceID', '');

      return [];
    }
  };

  const onInvoiceChanged = async (invoiceID: string) => {
    try {
      const result = await apiService.getPaymentFormDetailsByInvoice(invoiceID);
      if (result.errorCode < 0) {
        globalMessage.open(E.serverError);
      } else {
        await formik.setFieldValue('customerID', result.body.customerID || '');
        await formik.setFieldValue('customerName', result.body.customerName || '');
        await formik.setFieldValue('email', result.body.email || '');
        await formik.setFieldValue('phone', result.body.phone || '');
        await formik.setFieldValue('amount', result.body.amount || 0);
      }
    } catch (e) {
      globalMessage.open(E.serverError);
    }
  };

  const loadCustomerOptions = async (input: string) => {
    try {
      const result = await apiService.getCustomerOptions(input);
      if (result.errorCode < 0) {
        globalMessage.open(M.serverError);
        return [];
      } else {
        return result.body.list;
      }
    } catch (e) {
      globalMessage.open(M.serverError);
      return [];
    }
  };

  const onCustomerChanged = async (customerID: string) => {
    try {
      const result = await apiService.getPaymentFormDetailsByCustomerID(customerID);
      if (result.errorCode < 0) {
        globalMessage.open(E.serverError);
      } else {
        await formik.setFieldValue('customerID', result.body._id || '');
        await formik.setFieldValue('customerName', result.body.customerName || '');
        await formik.setFieldValue('email', result.body.email || '');
        await formik.setFieldValue('phone', result.body.phone || '');
      }
    } catch (e) {
      globalMessage.open(E.serverError);
    }
  };

  const goToPaypal = async () => {
    if (paypalLink) {
      window.open(paypalLink, '_blank');
    }
  };

  const context: PaymentPaypalContext = {
    ...defaultContext,
    currencyOptions,
    loadInvoiceOptions,
    onInvoiceChanged,
    loadCustomerOptions,
    onCustomerChanged,
    updateForm,
    loading,
    paypalLink,
    goToPaypal
  };

  return (
    <PaymentPaypalContext.Provider value={context}>
      <FormikProvider value={formik}>
        <Form>
          {children}
        </Form>
      </FormikProvider>
    </PaymentPaypalContext.Provider>
  );
};
